/**
 * Vorlage „Ruhen lassen“.
 *
 * Gebratenes Fleisch ruht locker mit Alufolie abgedeckt auf dem Brett, ein
 * Teig ruht in Folie gewickelt. Die Uhr zeigt die Ruhezeit; danach wird die
 * Abdeckung abgenommen.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { path, roundedRectPath } from '../paths.ts';
import { compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { steam } from '../parts/effects.ts';
import { doughBall, meatPiece } from '../parts/food.ts';
import { arrow, BADGE_RIGHT, counter, durationLabel, GROUND_Y, shadow, timerBadge } from '../parts/stage.ts';
import { minutesFromText } from '../inference.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

/** Brett in der Seitenansicht. */
const BOARD = { x: 30, width: 156, height: 8 } as const;
const BOARD_TOP = GROUND_Y - BOARD.height;
const CX = BOARD.x + BOARD.width / 2;

/** Zeitplan. */
const TIME = { coverOn: [0.04, 0.18], coverOff: [0.82, 0.94] } as const;

/** Was ruht. */
const ITEMS = { meat: 'Fleisch', dough: 'Teig' } as const;
type Item = keyof typeof ITEMS;

/** Zelt aus Alufolie mit Knitterlinien. */
function foilTent(): SceneNode[] {
  const tent = path()
    .move(CX - 62, BOARD_TOP)
    .quad(CX - 58, BOARD_TOP - 34, CX - 16, BOARD_TOP - 44)
    .line(CX + 14, BOARD_TOP - 46)
    .quad(CX + 58, BOARD_TOP - 36, CX + 62, BOARD_TOP)
    .close()
    .toString();
  const creases: SceneNode[] = [
    path()
      .move(CX - 40, BOARD_TOP - 6)
      .line(CX - 30, BOARD_TOP - 30)
      .line(CX - 18, BOARD_TOP - 38)
      .toString(),
    path()
      .move(CX + 6, BOARD_TOP - 4)
      .line(CX + 2, BOARD_TOP - 26)
      .line(CX + 12, BOARD_TOP - 40)
      .toString(),
    path()
      .move(CX + 40, BOARD_TOP - 8)
      .line(CX + 34, BOARD_TOP - 26)
      .toString(),
  ].map((d) => shape(d, styles.fine('deep')));
  return [shape(tent, styles.solid('tint')), group(creases, { clip: tent }), shape(tent, styles.outline())];
}

/** Frischhaltefolie um den Teig: heller Rand und Falten. */
function wrap(): SceneNode[] {
  const outline = doughBall(CX, BOARD_TOP, 84, 40);
  return [
    shape(outline, { fill: 'none', stroke: 'paper', strokeWidth: STROKE.outline }),
    shape(
      path()
        .move(CX - 30, BOARD_TOP - 30)
        .quad(CX - 10, BOARD_TOP - 40, CX + 10, BOARD_TOP - 38)
        .toString(),
      styles.fine('paper'),
    ),
    shape(
      path()
        .move(CX + 14, BOARD_TOP - 34)
        .line(CX + 30, BOARD_TOP - 22)
        .toString(),
      styles.fine('paper'),
    ),
    shape(roundedRectPath(CX + 34, BOARD_TOP - 10, 14, 8, 3), styles.solid('paper')),
  ];
}

/** Vorlage „Ruhen lassen“. */
export const prepRestTemplate = defineTemplate({
  id: 'prep.rest',
  group: 'prep',
  title: 'Ruhen lassen',
  description:
    'Fleisch ruht nach dem Braten locker mit Folie abgedeckt, damit sich der Saft verteilt. Teig ruht in Folie gewickelt, damit er sich entspannt.',
  durationSeconds: 5,
  keywords: [
    'ruhen lassen',
    'ruhen',
    'nachziehen',
    'durchziehen',
    'beiseitestellen',
    'alufolie',
    'frischhaltefolie',
  ],
  stillTime: 0.5,
  parameters: [
    choice('item', 'Was ruht', ITEMS, 'meat'),
    {
      kind: 'number',
      name: 'minutes',
      label: 'Dauer',
      min: 1,
      max: 240,
      step: 1,
      unit: 'Min',
      defaultValue: 10,
    },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      minutes: minutesFromText(text),
      item: lower.includes('teig')
        ? 'dough'
        : /fleisch|braten|steak|filet|keule/.test(lower)
          ? 'meat'
          : undefined,
    };
  },
  scene(values, t) {
    const item = textValue(values, 'item', 'meat') as Item;
    const minutes = numberValue(values, 'minutes', 10);
    const cover =
      ease.inOutCubic(segment(t, ...TIME.coverOn)) - ease.inOutCubic(segment(t, ...TIME.coverOff));
    const resting = segment(t, TIME.coverOn[1], TIME.coverOff[0]);
    const food: SceneNode[] =
      item === 'meat'
        ? [meatPiece(CX, BOARD_TOP - 13, 92, 26, 0.5, 0.5)]
        : [shape(doughBall(CX, BOARD_TOP, 80, 36), styles.snow(STROKE.outline))];
    // Folie senkt sich von oben ab und wird am Ende wieder abgehoben.
    const cloth = item === 'meat' ? foilTent() : wrap();
    const lift = lerp(-60, 0, cover);
    return compact([
      ...counter(),
      shadow(CX, BOARD.width * 0.46),
      shape(roundedRectPath(BOARD.x, BOARD_TOP, BOARD.width, BOARD.height + 2, 3), styles.solid('shade')),
      ...food,
      ...(item === 'meat'
        ? steam(CX, BOARD_TOP - 32, t, {
            count: 2,
            spread: 26,
            height: 22,
            strength: 0.6,
          })
        : []),
      faded(Math.min(1, cover * 1.5), [group(cloth, { transform: { y: lift } })]),
      timerBadge(BADGE_RIGHT, 26, durationLabel(minutes), resting),
    ]);
  },
  hints() {
    return [
      arrow([
        { x: CX, y: 30 },
        { x: CX, y: 60 },
      ]),
    ];
  },
});
